/**
 * RadarWidget — animated SVG radar + HUD overlay.
 * Pure SVG + CSS animation; no runtime cost.
 */
export default function RadarWidget({ className = "" }) {
  return (
    <div
      className={`relative w-full aspect-square ${className}`}
      data-testid="radar-widget"
    >
      <svg viewBox="0 0 200 200" className="w-full h-full">
        {/* Outer ring */}
        <circle
          cx="100"
          cy="100"
          r="96"
          fill="none"
          stroke="rgb(var(--primary) / 0.35)"
          strokeWidth="1"
        />
        <circle
          cx="100"
          cy="100"
          r="78"
          fill="none"
          stroke="rgb(var(--primary) / 0.22)"
          strokeWidth="1"
          strokeDasharray="2 4"
        />
        <circle
          cx="100"
          cy="100"
          r="56"
          fill="none"
          stroke="rgb(var(--primary) / 0.18)"
          strokeWidth="1"
        />
        <circle
          cx="100"
          cy="100"
          r="30"
          fill="none"
          stroke="rgb(var(--primary) / 0.18)"
          strokeWidth="1"
          strokeDasharray="2 3"
        />

        {/* Cross hairs */}
        <line x1="0" y1="100" x2="200" y2="100" stroke="rgb(var(--primary) / 0.16)" strokeWidth="1" />
        <line x1="100" y1="0" x2="100" y2="200" stroke="rgb(var(--primary) / 0.16)" strokeWidth="1" />

        {/* Tick marks around outer ring */}
        {Array.from({ length: 24 }).map((_, i) => {
          const angle = (i * 360) / 24;
          const rad = (angle * Math.PI) / 180;
          const x1 = 100 + Math.cos(rad) * 96;
          const y1 = 100 + Math.sin(rad) * 96;
          const x2 = 100 + Math.cos(rad) * (i % 6 === 0 ? 86 : 92);
          const y2 = 100 + Math.sin(rad) * (i % 6 === 0 ? 86 : 92);
          return (
            <line
              key={i}
              x1={x1}
              y1={y1}
              x2={x2}
              y2={y2}
              stroke="rgb(var(--accent) / 0.6)"
              strokeWidth="1"
            />
          );
        })}

        {/* Rotating sweep wedge */}
        <g className="radar-sweep" style={{ transformOrigin: "100px 100px" }}>
          <defs>
            <linearGradient id="sweepGrad" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="rgb(var(--accent))" stopOpacity="0.65" />
              <stop offset="100%" stopColor="rgb(var(--accent))" stopOpacity="0" />
            </linearGradient>
          </defs>
          <path
            d="M100,100 L196,100 A96,96 0 0,0 152,16 Z"
            fill="url(#sweepGrad)"
          />
        </g>

        {/* Data points */}
        <g>
          <circle cx="140" cy="60" r="3" fill="rgb(var(--accent))">
            <animate attributeName="opacity" values="1;0.3;1" dur="2s" repeatCount="indefinite" />
          </circle>
          <circle cx="62" cy="138" r="2.5" fill="rgb(var(--accent))">
            <animate attributeName="opacity" values="1;0.2;1" dur="1.6s" repeatCount="indefinite" />
          </circle>
          <circle cx="148" cy="146" r="2" fill="rgb(var(--accent))">
            <animate attributeName="opacity" values="1;0.25;1" dur="2.4s" repeatCount="indefinite" />
          </circle>
          <circle cx="74" cy="66" r="2" fill="rgb(var(--accent))">
            <animate attributeName="opacity" values="0.3;1;0.3" dur="1.9s" repeatCount="indefinite" />
          </circle>
        </g>

        {/* Center dot */}
        <circle cx="100" cy="100" r="4" fill="rgb(var(--accent))" />
        <circle cx="100" cy="100" r="8" fill="none" stroke="rgb(var(--accent))" strokeWidth="1">
          <animate attributeName="r" values="8;16;8" dur="2.4s" repeatCount="indefinite" />
          <animate attributeName="opacity" values="1;0;1" dur="2.4s" repeatCount="indefinite" />
        </circle>
      </svg>

      {/* Corner brackets around radar */}
      <span className="absolute top-0 left-0 w-3 h-3 border-t border-l border-[rgb(var(--accent))]" />
      <span className="absolute top-0 right-0 w-3 h-3 border-t border-r border-[rgb(var(--accent))]" />
      <span className="absolute bottom-0 left-0 w-3 h-3 border-b border-l border-[rgb(var(--accent))]" />
      <span className="absolute bottom-0 right-0 w-3 h-3 border-b border-r border-[rgb(var(--accent))]" />

      {/* Labels */}
      <div className="absolute -top-5 left-0 right-0 flex items-center justify-between font-mono text-[9px] tracking-[0.28em] uppercase text-[rgb(var(--text-dim))]">
        <span>// RADAR.LIVE</span>
        <span className="text-[rgb(var(--accent))]">SCAN.ACTIVE</span>
      </div>
      <div className="absolute -bottom-5 left-0 right-0 flex items-center justify-between font-mono text-[9px] tracking-[0.28em] uppercase text-[rgb(var(--text-dim))]">
        <span>AZI 248°</span>
        <span>RANGE 2.4KM</span>
      </div>
    </div>
  );
}
